import React, {
    useContext, useEffect, useMemo, useRef, useState 
} from 'react';
import {
    Box, Typography, useTheme 
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import CardContainer from '../../components/cardContainer';
import Card from '../../components/card';
import dataDogLogger from '../../../../lib/datadog';
import {
    DiagnosticFormContext 
} from '../../../../contexts/form';
import {
    TypographyVariants, TypographyWeights 
} from '../../../../figmaTheme';
import SunnysideButton, {
    ButtonSizes, ButtonVariants 
} from '../../../../components_sunnyside/button';

const ENVIRONMENT = process.env.NEXT_PUBLIC_NENV;
const skippyEnvironment = new Set(['development', 'staging']);

export const IntroFlow = (): JSX.Element => {
    const [titleHeight, setTitleHeight] = useState(null);
    const [sunnysideHeight, setSunnysideHeight] = useState(null);
    const [benefitsHeight, setBenefitsHeight] = useState(null);
    const initialScreenHeight = useMemo(() => {
        if (typeof window !== 'undefined') {
            return window.innerHeight;
        }
        return 0;
    }, []);
    const [screenHeight] = useState(initialScreenHeight);
    const useStyles = useMemo(() => makeStyles((theme) => ({
        // Shared classes
        scrollableContainer: {
            overflowY: 'scroll',
            overflowX: 'hidden',
            scrollbarWidth: 'none',
            minHeight: '100%',
            width: '100vw',
            maxWidth: '692px',
            transform: 'translate(-24px, 0)',
            padding: '0 24px'
        },
        content: {
            transform: 'translate(0, 80px)',
        },
        subcontent: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            margin: 'auto 0',
        },
        skippy: {
            position: 'absolute',
            backgroundColor: theme.palette.error.main,
            margin: '0 auto',
            transform: 'translate(0, -36px)'
        },
        sunnysideTitle: {
            fontWeight: TypographyWeights.BOLD,
            textAlign: 'center'
        },
        sunnysideSubtitle: {
            textAlign: 'center'
        },
        benefit: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center'
        },
        icon: {
            flexShrink: 0,
            width: '16px',
            height: '16px',
            transform: 'rotate(0)',
        },
        // Step 1 Classes
        title1: {
            position: 'absolute',
            textAlign: 'center',
            opacity: 0,
            transform: `translate(0, ${((screenHeight - 146) - titleHeight) / 2}px)`,
            animation: '$fadeIn 0.5s forwards 0s, $shiftTitle 1s forwards 1.3s'
        },
        solution1: {
            position: 'absolute',
            width: '237px',
            margin: '0 auto',
            textAlign: 'center',
            zIndex: 1,
            opacity: 0,
        },
        sunnyside1: {
            padding: '24px 0',
            display: 'flex',
            flexDirection: 'column',
            rowGap: '16px',
            zIndex: 1,
            backgroundColor: theme.palette.white,
            opacity: 0,
            transform: 'translate(0, 195px)',
        },
        circleTakeover1: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: theme.palette.tertiary['150'],
            borderRadius: '50%',
            width: '692px',
            height: '692px',
            transform: 'translate(0, 100vh)',
            animation: '$slideUp 1s forwards 1.3s'
        },
        circleContent1: {
            display: 'flex',
            flexDirection: 'column',
            rowGap: '16px',
            width: 'calc(100vw - 90px)',
            maxWidth: '290px',
            transform: `translate(0, -${Math.max(0, (692 - benefitsHeight) / 2) - 70}px)`
        },
        benefit1: {
            opacity: 0,
            transform: 'translate(0, 100vh)',
            animation: '$slideBenefitUp 0.5s forwards 2.5s, $fadeIn 0.25s forwards 2.75s'
        },
        benefit2: {
            opacity: 0,
            transform: 'translate(0, 100vh)',
            animation: '$slideBenefitUp 0.5s forwards 3.25s, $fadeIn 0.25s forwards 3.5s'
        },
        benefit3: {
            opacity: 0,
            transform: 'translate(0, 100vh)',
            animation: '$slideBenefitUp 0.5s forwards 4s, $fadeIn 0.25s forwards 4.25s'
        },
        benefit4: {
            opacity: 0,
            transform: 'translate(0, 100vh)',
            animation: '$slideBenefitUp 0.5s forwards 4.75s, $fadeIn 0.25s forwards 5s'
        },
        benefit5: {
            opacity: 0,
            transform: 'translate(0, 100vh)',
            animation: '$slideBenefitUp 0.5s forwards 5.5s, $fadeIn 0.25s forwards 5.75s'
        },
        benefit6: {
            opacity: 0,
            transform: 'translate(0, 100vh)',
            animation: '$slideBenefitUp 0.5s forwards 6.25s, $fadeIn 0.25s forwards 6.5s'
        },
        benefit7: {
            opacity: 0,
            transform: 'translate(0, 100vh)',
            animation: '$slideBenefitUp 0.5s forwards 7s, $fadeIn 0.25s forwards 7.25s'
        },
        benefitText1: {
            position: 'absolute',
            transform: 'translate(32px, 0)',
        },
        // Step 2 Classes
        title2: {
            position: 'absolute',
            textAlign: 'center',
            opacity: 1,
            transform: 'translate(0)',
            animation: '$fadeOut 0.5s forwards 1s'
        },
        circleTakeover2: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: theme.palette.tertiary['150'],
            borderRadius: '50%',
            width: '692px',
            height: '692px',
            transition: 'width 0.5s, height 0.5s',
            transform: `translate(0, ${-sunnysideHeight + titleHeight + 34}px)`,
            animation: '$shrink 0.5s forwards 1s, $moveCircle1 1s forwards 1.5s'
        },
        circleContent2: {
            display: 'flex',
            flexDirection: 'column',
            rowGap: '16px',
            width: 'calc(100vw - 90px)',
            maxWidth: '290px',
            transform: `translate(0, -${Math.max(0, (692 - benefitsHeight) / 2) - 70}px)`,
            animation: '$shrinkContent 0.5s forwards 1s, $shiftContent 0.5s forwards 1s, $rotate 1s forwards 1.5s'
        },
        icon1: {
            flexShrink: 0,
            width: '16px',
            height: '16px',
            transform: 'rotate(0)',
            animation: '$rotateIcon 1s forwards 1.5s, $fadeOut 0.5s forwards 3s'
        },
        icon2: {
            flexShrink: 0,
            width: '16px',
            height: '16px',
            transform: 'rotate(0)',
            animation: '$rotateIcon 1s forwards 1.5s, $fadeOut 0.5s forwards 3s'
        },
        icon3: {
            flexShrink: 0,
            width: '16px',
            height: '16px',
            transform: 'rotate(0)',
            animation: '$rotateIcon 1s forwards 1.5s, $fadeOut 0.5s forwards 3s'
        },
        icon4: {
            flexShrink: 0,
            width: '16px',
            height: '16px',
            transform: 'rotate(0)',
            animation: '$rotateIcon 1s forwards 1.5s, $fadeOut 0.5s forwards 3s'
        },
        icon5: {
            flexShrink: 0,
            width: '16px',
            height: '16px',
            transform: 'rotate(0)',
            animation: '$rotateIcon 1s forwards 1.5s, $fadeOut 0.5s forwards 3s'
        },
        icon6: {
            flexShrink: 0,
            width: '16px',
            height: '16px',
            transform: 'rotate(0)',
            animation: '$rotateIcon 1s forwards 1.5s, $fadeOut 0.5s forwards 3s'
        },
        icon7: {
            flexShrink: 0,
            width: '16px',
            height: '16px',
            transform: 'rotate(0)',
            animation: '$rotateIcon 1s forwards 1.5s, $fadeOut 0.5s forwards 3s'
        },
        benefitText2: {
            position: 'absolute',
            transform: 'translate(32px, 0)',
            animation: '$fadeOutText 0.25s forwards 1s'
        },
        // Step 3 Classes
        title3: {
            position: 'absolute',
            textAlign: 'center',
            opacity: 0,
            transform: 'translate(0)',
        },
        solution2: {
            position: 'absolute',
            width: '237px',
            margin: '0 auto',
            textAlign: 'center',
            zIndex: 1,
            opacity: 0,
            animation: '$fadeIn 1.25s forwards 0.5s, $fadeOut 1s forwards 2.75s'
        },
        sunnyside2: {
            padding: '24px 0',
            display: 'flex',
            flexDirection: 'column',
            rowGap: '16px',
            zIndex: 1,
            backgroundColor: theme.palette.white,
            opacity: 0,
            transform: 'translate(0, 195px)',
            animation: '$fadeIn 0.5s forwards 4.5s'
        },
        circleTakeover3: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: theme.palette.tertiary['150'],
            borderRadius: '50%',
            width: '313px',
            height: '313px',
            transform: `translate(0, ${-(sunnysideHeight) + 50}px)`,
            animation: '$moveCircle2 0.5s linear forwards 3.5s, $changeColor 1s forwards 4.5s, $moveCircle3 1s forwards 4.5s'
        },
        circleContent3: {
            display: 'flex',
            flexDirection: 'column',
            rowGap: '16px',
            width: '16px',
            maxWidth: '290px',
            transform: 'rotate(-90deg)',
        },
        benefitA: {
            opacity: 1,
            transform: 'translate(0)'
        },
        iconA: {
            flexShrink: 0,
            width: '16px',
            height: '16px',
            opacity: 0,
        },
        benefitText3: {
            position: 'absolute',
            transform: 'translate(32px, 0)',
            opacity: 0
        },
        '@keyframes fadeIn': {
            '0%': {
                opacity: '0' 
            },
            '100%': {
                opacity: '1' 
            },
        },
        '@keyframes fadeOut': {
            '0%': {
                opacity: '1' 
            },
            '100%': {
                opacity: '0' 
            },
        },
        '@keyframes shiftTitle': {
            '0%': {
                transform: `translate(0, ${((screenHeight - 146) - titleHeight) / 2}px)` 
            },
            '100%': {
                transform: 'translate(0)' 
            },
        },
        '@keyframes fadeOutText': {
            '0%': {
                opacity: '1' 
            },
            '100%': {
                opacity: '0' 
            },
        },
        '@keyframes slideUp': {
            '0%': {
                transform: 'translate(0, 100vh)' 
            },
            '100%': {
                transform: `translate(0, ${-sunnysideHeight + titleHeight + 34}px)` 
            },
        },
        '@keyframes slideBenefitUp': {
            '0%': {
                transform: 'translate(0, 100vh)' 
            },
            '100%': {
                transform: 'translate(0)' 
            },
        },
        '@keyframes rotate': {
            '0%': {
                transform: 'translate(0)' 
            },
            '100%': {
                transform: 'rotate(-90deg)' 
            },
        },
        '@keyframes shrinkContent': {
            '0%': {
                width: 'calc(100vw - 45px)' 
            },
            '100%': {
                width: '16px' 
            },
        },
        '@keyframes shiftContent': {
            '0%': {
                transform: `translate(0, -${Math.max(0, (692 - benefitsHeight) / 2) - 70}px)` 
            },
            '100%': {
                transform: 'translate(0)' 
            },
        },
        '@keyframes rotateIcon': {
            '0%': {
                transform: 'rotate(0)' 
            },
            '100%': {
                transform: 'rotate(90deg)' 
            },
        },
        '@keyframes shrink': {
            '0%': {
                width: '692px',
                height: '692px' 
            },
            '100%': {
                width: '313px',
                height: '313px' 
            },
        },
        '@keyframes moveCircle1': {
            '0%': {
                transform: `translate(0, ${-sunnysideHeight + titleHeight + 34}px)` 
            },
            '100%': {
                transform: `translate(0, ${-(sunnysideHeight) + 50}px)` 
            },
        },
        '@keyframes moveCircle2': {
            '0%': {
                width: '313px',
                height: '313px',
                transform: `translate(0, ${-(sunnysideHeight) + 50}px)` 
            },
            '100%': {
                width: '145px',
                height: '145px',
                transform: `translate(0, ${-(sunnysideHeight)}px)` 
            },
        },
        '@keyframes moveCircle3': {
            '0%': {
                width: '145px',
                height: '145px',
                transform: `translate(0, ${-(sunnysideHeight)}px)` 
            },
            '100%': {
                width: '145px',
                height: '145px',
                transform: `translate(0, ${-(sunnysideHeight) + 150}px)` 
            },
        },
        '@keyframes changeColor': {
            '0%': {
                backgroundColor: theme.palette.tertiary['150'] 
            },
            '100%': {
                backgroundColor: theme.palette.tertiary.main 
            },
        },
    })), [titleHeight, screenHeight]);
    const formContext = useContext(DiagnosticFormContext);
    const classes = useStyles();
    const theme = useTheme();

    useEffect(() => {
        if (!formContext.startTime) return;

        const endTime = new Date().getTime();
        const diff = endTime - formContext.startTime;

        dataDogLogger(
            'info',
            `Diagnostic screen introFlow rendered in ${diff}ms`,
            {
                frontendMetric: diff,
                url: 'introFlow',
                rawData: {
                    formId: formContext?.formId
                }
            }
        );
    }, [formContext.startTime]);

    const titleElement = useRef(null);

    useEffect(() => {
        if (!titleElement.current) return;
        setTitleHeight(titleElement.current.clientHeight);
    }, [titleElement]);

    const sunnysideElement = useRef(null);

    useEffect(() => {
        if (!sunnysideElement.current) return;
        setSunnysideHeight(sunnysideElement.current.clientHeight);
    }, [sunnysideElement]);

    const benefits = useRef(null);

    useEffect(() => {
        if (!benefits.current) return;
        setBenefitsHeight(benefits.current.clientHeight);
    }, [benefits]);

    const [step, setStep] = useState(0);
    const [showButton, setShowButton] = useState(false);

    useEffect(() => {
        if (step === 0) {
            setTimeout(() => {
                setStep(1);
            }, 7500);
        } else if (step === 1) {
            setTimeout(() => {
                setStep(2);
            }, 3500);
        } else {
            setTimeout(() => {
                setShowButton(true);
            }, 5000);
        }
    }, [step]);

    const CONTENT = useMemo(() => [
        (
            <Box
                key={0}
                className={classes.subcontent}
            >
                <Typography
                    ref={titleElement}
                    variant={TypographyVariants.H2}
                    className={classes.title1}
                    dangerouslySetInnerHTML={{
                        __html: `What if you could change <span style="font-weight:${TypographyWeights.BOLD};">one habit</span> to unlock improvements across every area of your life?` 
                    }}
                />
                <Box
                    ref={sunnysideElement}
                    className={classes.sunnyside1}
                >
                    <Typography
                        variant={TypographyVariants.H2}
                        className={classes.sunnysideTitle}
                        dangerouslySetInnerHTML={{
                            __html: `With Sunnyside, we'll help you reach your <span style="color:${theme.palette.primary.main};">healthier drinking goals</span>, no matter what they are.` 
                        }}
                    />
                    <Typography
                        variant={TypographyVariants.H5}
                        className={classes.sunnysideSubtitle}
                        dangerouslySetInnerHTML={{
                            __html: 'Get a plan that\'s 100% personalized to you by completing this short quiz.' 
                        }}
                    />
                </Box>
                <Box
                    key={0}
                    className={classes.circleTakeover1}
                >
                    <Box
                        ref={benefits}
                        className={classes.circleContent1}
                    >
                        <Box className={`${classes.benefit} ${classes.benefit1}`}>
                            <svg
                                className={classes.icon}
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <g clipPath="url(#clip0_4069_27486)">
                                    <path
                                        d="M9.34954 11.531C9.30153 11.595 9.23889 11.6465 9.16686 11.6812C9.09483 11.7159 9.01552 11.7329 8.93559 11.7306C8.85566 11.7284 8.77743 11.707 8.70747 11.6683C8.63752 11.6295 8.57787 11.5746 8.53354 11.508L6.06454 7.80402L5.49454 8.75702C5.45017 8.83106 5.38738 8.89236 5.31229 8.93495C5.2372 8.97753 5.15236 8.99995 5.06604 9.00002H1.97754C4.16254 12.317 7.99954 15.482 7.99954 15.482C7.99954 15.482 11.8365 12.317 14.0215 9.00002H11.2495L9.34954 11.531Z"
                                        fill={theme.palette.neutral.main}
                                    />
                                    <path
                                        d="M15.09 7.00001C15.341 6.41699 15.4801 5.79195 15.5 5.15751C15.5118 4.27359 15.2365 3.4097 14.7155 2.69557C14.1945 1.98144 13.4559 1.45561 12.6105 1.19705C11.7652 0.938489 10.8588 0.961156 10.0275 1.26164C9.1961 1.56213 8.48467 2.12422 8 2.86351C7.62576 2.3029 7.12059 1.8419 6.5282 1.52035C5.9358 1.1988 5.27399 1.02638 4.6 1.01801C3.50772 1.02435 2.46258 1.46378 1.69392 2.23984C0.925257 3.01591 0.495876 4.06522 0.5 5.15751C0.520083 5.79198 0.659338 6.41701 0.9105 7.00001H0V8.00001H4.783L6.0225 5.93451L8.976 10.365L10.75 8.00001H16V7.00001H15.09Z"
                                        fill={theme.palette.neutral.main}
                                    />
                                </g>
                                <defs>
                                    <clipPath id="clip0_4069_27486">
                                        <rect
                                            width="16"
                                            height="16"
                                            fill={theme.palette.white}
                                        />
                                    </clipPath>
                                </defs>
                            </svg>
                            <Typography
                                variant={TypographyVariants.H5}
                                className={classes.benefitText1}
                            >
                                Improved Overall Health
                            </Typography>
                        </Box>
                        <Box className={`${classes.benefit} ${classes.benefit2}`}>
                            <svg
                                className={classes.icon}
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M8 16C12.4183 16 16 12.4183 16 8C16 3.58172 12.4183 0 8 0C3.58172 0 0 3.58172 0 8C0 12.4183 3.58172 16 8 16ZM8.8 4C8.8 3.55817 8.44183 3.2 8 3.2C7.55817 3.2 7.2 3.55817 7.2 4V8.8C7.2 9.06748 7.33368 9.31727 7.55624 9.46564L9.95624 11.0656C10.3239 11.3107 10.8206 11.2114 11.0656 10.8438C11.3107 10.4761 11.2114 9.97944 10.8438 9.73436L8.8 8.37185V4Z"
                                    fill={theme.palette.neutral.main}
                                />
                            </svg>
                            <Typography
                                variant={TypographyVariants.H5}
                                className={classes.benefitText1}
                            >
                                Increased Productivity
                            </Typography>
                        </Box>
                        <Box className={`${classes.benefit} ${classes.benefit3}`}>
                            <svg
                                className={classes.icon}
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <g clipPath="url(#clip0_4069_27502)">
                                    <g clipPath="url(#clip1_4069_27502)">
                                        <path
                                            d="M6 0C2.5 0.9 0 4.1 0 7.9C0 12.4 3.6 16 8.1 16C11.9 16 15 13.5 16 10C9.9 11.7 4.3 6.1 6 0Z"
                                            fill={theme.palette.neutral.main}
                                        />
                                    </g>
                                </g>
                                <defs>
                                    <clipPath id="clip0_4069_27502">
                                        <rect
                                            width="16"
                                            height="16"
                                            fill={theme.palette.white}
                                        />
                                    </clipPath>
                                    <clipPath id="clip1_4069_27502">
                                        <rect
                                            width="16"
                                            height="16"
                                            fill={theme.palette.white}
                                        />
                                    </clipPath>
                                </defs>
                            </svg>
                            <Typography
                                variant={TypographyVariants.H5}
                                className={classes.benefitText1}
                            >
                                More Restful Sleep
                            </Typography>
                        </Box>
                        <Box className={`${classes.benefit} ${classes.benefit4}`}>
                            <svg
                                className={classes.icon}
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M9.59508 6.42088C9.59508 6.42088 10.2853 2.361 7.46892 0.59845C7.42696 1.29602 7.23831 1.97689 6.91531 2.59659C6.59231 3.21629 6.14221 3.76088 5.59441 4.19479C4.39162 5.25232 2.12945 7.63014 2.15351 10.1653C2.14487 11.249 2.43924 12.3135 3.00342 13.2388C3.5676 14.1641 4.37913 14.9134 5.34645 15.402C5.38101 14.9127 5.51289 14.4352 5.73436 13.9975C5.95583 13.5598 6.26244 13.1707 6.63621 12.853C6.95294 12.6094 7.218 12.3052 7.41593 11.9582C7.61386 11.6111 7.74069 11.228 7.78904 10.8314C8.61705 11.2716 9.31665 11.9191 9.81939 12.7107C10.3221 13.5023 10.6108 14.4108 10.6572 15.3474V15.3595C11.5702 14.9412 12.3498 14.2786 12.9095 13.4448C13.4693 12.611 13.7875 11.6387 13.8289 10.6353C14.0287 8.25374 12.7242 5.01546 11.5667 3.95701C11.1297 4.93261 10.4511 5.78063 9.59508 6.42088Z"
                                    fill={theme.palette.neutral['600']}
                                />
                            </svg>
                            <Typography
                                variant={TypographyVariants.H5}
                                className={classes.benefitText1}
                            >
                                Weight Loss and Fitness Gains
                            </Typography>
                        </Box>
                        <Box className={`${classes.benefit} ${classes.benefit5}`}>
                            <svg
                                className={classes.icon}
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <g clipPath="url(#clip0_4318_10941)">
                                    <path
                                        d="M8 0C3.6 0 0 3.6 0 8C0 12.4 3.6 16 8 16C12.4 16 16 12.4 16 8C16 3.6 12.4 0 8 0ZM6 5C6.6 5 7 5.4 7 6C7 6.6 6.6 7 6 7C5.4 7 5 6.6 5 6C5 5.4 5.4 5 6 5ZM8 12C6.3 12 5 10.7 5 9H11C11 10.7 9.7 12 8 12ZM10 7C9.4 7 9 6.6 9 6C9 5.4 9.4 5 10 5C10.6 5 11 5.4 11 6C11 6.6 10.6 7 10 7Z"
                                        fill={theme.palette.neutral.main}
                                    />
                                </g>
                                <defs>
                                    <clipPath id="clip0_4318_10941">
                                        <rect
                                            width="16"
                                            height="16"
                                            fill={theme.palette.white}
                                        />
                                    </clipPath>
                                </defs>
                            </svg>
                            <Typography
                                variant={TypographyVariants.H5}
                                className={classes.benefitText1}
                            >
                                Better Mood and Mental Health
                            </Typography>
                        </Box>
                        <Box className={`${classes.benefit} ${classes.benefit6}`}>
                            <svg
                                className={classes.icon}
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <g clipPath="url(#clip0_4069_27531)">
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M5.14286 3.71426C5.14286 2.1363 6.42204 0.857117 8 0.857117C9.57796 0.857117 10.8571 2.1363 10.8571 3.71426C10.8571 5.29222 9.57796 6.5714 8 6.5714C6.42204 6.5714 5.14286 5.29222 5.14286 3.71426Z"
                                        fill={theme.palette.neutral.main}
                                    />
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M10.8571 5.99997C10.8571 4.73761 11.8805 3.71426 13.1429 3.71426C14.4052 3.71426 15.4286 4.73761 15.4286 5.99997C15.4286 7.26234 14.4052 8.28569 13.1429 8.28569C11.8805 8.28569 10.8571 7.26234 10.8571 5.99997Z"
                                        fill={theme.palette.neutral.main}
                                    />
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M0.571429 5.99997C0.571429 4.73761 1.59478 3.71426 2.85714 3.71426C4.11951 3.71426 5.14286 4.73761 5.14286 5.99997C5.14286 7.26234 4.11951 8.28569 2.85714 8.28569C1.59478 8.28569 0.571429 7.26234 0.571429 5.99997Z"
                                        fill={theme.palette.neutral.main}
                                    />
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M3.66466 10.0895C4.5774 8.66238 6.17779 7.71426 8 7.71426C9.82244 7.71426 11.423 8.66261 12.3357 10.09C12.9667 11.0769 13.2396 12.2668 13.1116 13.427C13.0916 13.6083 12.9862 13.7691 12.828 13.8599C11.4053 14.6763 9.75625 15.1428 8 15.1428C6.24375 15.1428 4.59473 14.6763 3.172 13.8599C3.01378 13.7691 2.90842 13.6083 2.88841 13.427C2.76033 12.2666 3.03346 11.0764 3.66466 10.0895Z"
                                        fill={theme.palette.neutral.main}
                                    />
                                    <path
                                        d="M2.72936 9.43135C2.72018 9.44545 2.71106 9.45958 2.702 9.47375C1.96641 10.6239 1.62977 11.9936 1.73311 13.344C1.26984 13.2736 0.82034 13.1604 0.388898 13.0085L0.30131 12.9776C0.14153 12.9214 0.0298875 12.7763 0.0164503 12.6074L0.00908429 12.5148C0.00306068 12.4391 0 12.3627 0 12.2857C0 10.7506 1.2107 9.49819 2.72936 9.43135Z"
                                        fill={theme.palette.neutral.main}
                                    />
                                    <path
                                        d="M14.2672 13.3439C14.3705 11.9939 14.034 10.6244 13.2987 9.47439C13.2895 9.46001 13.2802 9.44567 13.2709 9.43137C14.7894 9.49835 16 10.7506 16 12.2857C16 12.3627 15.9969 12.4391 15.9909 12.5148L15.9835 12.6074C15.9701 12.7763 15.8585 12.9214 15.6987 12.9776L15.6111 13.0085C15.1797 13.1604 14.7303 13.2736 14.2672 13.3439Z"
                                        fill={theme.palette.neutral.main}
                                    />
                                </g>
                                <defs>
                                    <clipPath id="clip0_4069_27531">
                                        <rect
                                            width="16"
                                            height="16"
                                            fill={theme.palette.white}
                                        />
                                    </clipPath>
                                </defs>
                            </svg>
                            <Typography
                                variant={TypographyVariants.H5}
                                className={classes.benefitText1}
                            >
                                Stronger Relationships
                            </Typography>
                        </Box>
                        <Box className={`${classes.benefit} ${classes.benefit7}`}>
                            <svg
                                className={classes.icon}
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M9.82604 8.76896C10.6527 9.22177 11.6049 9.39172 12.5372 9.25288C13.4695 9.11405 14.3308 8.67405 14.9896 8.00001C14.5874 7.58828 14.1068 7.26116 13.5762 7.03791C13.0457 6.81465 12.4758 6.69977 11.9002 6.70001C11.1138 6.70126 10.3412 6.90628 9.65773 7.29509C8.97422 7.68389 8.40314 8.2432 8.00019 8.91846C7.59724 8.2432 7.02617 7.68389 6.34266 7.29509C5.65915 6.90628 4.88654 6.70126 4.10019 6.70001C3.52457 6.69977 2.9547 6.81465 2.42414 7.03791C1.89357 7.26116 1.41301 7.58828 1.01074 8.00001C1.66963 8.67405 2.53092 9.11405 3.46322 9.25288C4.39552 9.39172 5.34765 9.22177 6.17434 8.76896C6.738 9.23788 7.12745 9.88285 7.27999 10.6H4.75019C4.65584 10.6001 4.56263 10.6206 4.47703 10.6603C4.39143 10.7 4.3155 10.7579 4.25452 10.8299C4.19353 10.9019 4.14895 10.9863 4.12387 11.0772C4.09879 11.1682 4.09382 11.2635 4.10929 11.3566L4.75929 15.2566C4.78453 15.4084 4.86278 15.5463 4.98013 15.6458C5.09749 15.7453 5.24633 15.7999 5.40019 15.8H10.6002C10.7541 15.7999 10.9029 15.7453 11.0203 15.6458C11.1376 15.5463 11.2159 15.4084 11.2411 15.2566L11.8911 11.3566C11.9066 11.2635 11.9016 11.1682 11.8765 11.0772C11.8514 10.9863 11.8069 10.9019 11.7459 10.8299C11.6849 10.7579 11.609 10.7 11.5234 10.6603C11.4378 10.6206 11.3445 10.6001 11.2502 10.6H8.72039C8.87293 9.88285 9.26239 9.23788 9.82604 8.76896Z"
                                    fill={theme.palette.neutral.main}
                                />
                                <path
                                    d="M8.0002 0.200012C7.35741 0.200012 6.72905 0.390621 6.19459 0.747736C5.66013 1.10485 5.24357 1.61243 4.99759 2.20629C4.7516 2.80015 4.68724 3.45362 4.81264 4.08406C4.93805 4.71449 5.24758 5.29359 5.7021 5.74811C6.15662 6.20263 6.73572 6.51216 7.36615 6.63756C7.99659 6.76297 8.65006 6.69861 9.24392 6.45262C9.83778 6.20664 10.3454 5.79007 10.7025 5.25562C11.0596 4.72116 11.2502 4.0928 11.2502 3.45001C11.2492 2.58838 10.9064 1.76232 10.2972 1.15306C9.68789 0.543785 8.86183 0.201044 8.0002 0.200012ZM8.6502 4.10001C8.6502 4.459 8.35918 4.75001 8.0002 4.75001C7.64121 4.75001 7.3502 4.459 7.3502 4.10001V2.80001C7.3502 2.44103 7.64121 2.15001 8.0002 2.15001C8.35918 2.15001 8.6502 2.44103 8.6502 2.80001V4.10001Z"
                                    fill={theme.palette.neutral.main}
                                />
                            </svg>
                            <Typography
                                variant={TypographyVariants.H5}
                                className={classes.benefitText1}
                            >
                                Saved Money
                            </Typography>
                        </Box>
                    </Box>
                    <Typography
                        variant={TypographyVariants.H2}
                        className={classes.solution1}
                        dangerouslySetInnerHTML={{
                            __html: `Cutting back on <span style="font-weight:${TypographyWeights.BOLD};">drinking</span> is that habit` 
                        }}
                    />
                </Box>
            </Box>
        ),
        (
            <Box
                key={1}
                className={classes.subcontent}
            >
                <Typography
                    variant={TypographyVariants.H2}
                    className={classes.title2}
                    dangerouslySetInnerHTML={{
                        __html: `What if you could change <span style="font-weight:${TypographyWeights.BOLD};">one habit</span> to unlock improvements across every area of your life?` 
                    }}
                />
                <Box className={classes.sunnyside1}>
                    <Typography
                        variant={TypographyVariants.H2}
                        className={classes.sunnysideTitle}
                        dangerouslySetInnerHTML={{
                            __html: `With Sunnyside, we'll help you reach your <span style="color:${theme.palette.primary.main};">healthier drinking goals</span>, no matter what they are.` 
                        }}
                    />
                    <Typography
                        variant={TypographyVariants.H5}
                        className={classes.sunnysideSubtitle}
                        dangerouslySetInnerHTML={{
                            __html: 'Get a plan that\'s 100% personalized to you by completing this short quiz.' 
                        }}
                    />
                </Box>
                <Box className={classes.circleTakeover2}>
                    <Box className={classes.circleContent2}>
                        <Box className={`${classes.benefit} ${classes.benefitA}`}>
                            <svg
                                className={classes.icon1}
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <g clipPath="url(#clip0_4069_27486)">
                                    <path
                                        d="M9.34954 11.531C9.30153 11.595 9.23889 11.6465 9.16686 11.6812C9.09483 11.7159 9.01552 11.7329 8.93559 11.7306C8.85566 11.7284 8.77743 11.707 8.70747 11.6683C8.63752 11.6295 8.57787 11.5746 8.53354 11.508L6.06454 7.80402L5.49454 8.75702C5.45017 8.83106 5.38738 8.89236 5.31229 8.93495C5.2372 8.97753 5.15236 8.99995 5.06604 9.00002H1.97754C4.16254 12.317 7.99954 15.482 7.99954 15.482C7.99954 15.482 11.8365 12.317 14.0215 9.00002H11.2495L9.34954 11.531Z"
                                        fill={theme.palette.neutral.main}
                                    />
                                    <path
                                        d="M15.09 7.00001C15.341 6.41699 15.4801 5.79195 15.5 5.15751C15.5118 4.27359 15.2365 3.4097 14.7155 2.69557C14.1945 1.98144 13.4559 1.45561 12.6105 1.19705C11.7652 0.938489 10.8588 0.961156 10.0275 1.26164C9.1961 1.56213 8.48467 2.12422 8 2.86351C7.62576 2.3029 7.12059 1.8419 6.5282 1.52035C5.9358 1.1988 5.27399 1.02638 4.6 1.01801C3.50772 1.02435 2.46258 1.46378 1.69392 2.23984C0.925257 3.01591 0.495876 4.06522 0.5 5.15751C0.520083 5.79198 0.659338 6.41701 0.9105 7.00001H0V8.00001H4.783L6.0225 5.93451L8.976 10.365L10.75 8.00001H16V7.00001H15.09Z"
                                        fill={theme.palette.neutral.main}
                                    />
                                </g>
                                <defs>
                                    <clipPath id="clip0_4069_27486">
                                        <rect
                                            width="16"
                                            height="16"
                                            fill={theme.palette.white}
                                        />
                                    </clipPath>
                                </defs>
                            </svg>
                            <Typography
                                variant={TypographyVariants.H5}
                                className={classes.benefitText2}
                            >
                                Improved Overall Health
                            </Typography>
                        </Box>
                        <Box className={`${classes.benefit} ${classes.benefitA}`}>
                            <svg
                                className={classes.icon2}
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M8 16C12.4183 16 16 12.4183 16 8C16 3.58172 12.4183 0 8 0C3.58172 0 0 3.58172 0 8C0 12.4183 3.58172 16 8 16ZM8.8 4C8.8 3.55817 8.44183 3.2 8 3.2C7.55817 3.2 7.2 3.55817 7.2 4V8.8C7.2 9.06748 7.33368 9.31727 7.55624 9.46564L9.95624 11.0656C10.3239 11.3107 10.8206 11.2114 11.0656 10.8438C11.3107 10.4761 11.2114 9.97944 10.8438 9.73436L8.8 8.37185V4Z"
                                    fill={theme.palette.neutral.main}
                                />
                            </svg>
                            <Typography
                                variant={TypographyVariants.H5}
                                className={classes.benefitText2}
                            >
                                Increased Productivity
                            </Typography>
                        </Box>
                        <Box className={`${classes.benefit} ${classes.benefitA}`}>
                            <svg
                                className={classes.icon3}
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <g clipPath="url(#clip0_4069_27502)">
                                    <g clipPath="url(#clip1_4069_27502)">
                                        <path
                                            d="M6 0C2.5 0.9 0 4.1 0 7.9C0 12.4 3.6 16 8.1 16C11.9 16 15 13.5 16 10C9.9 11.7 4.3 6.1 6 0Z"
                                            fill={theme.palette.neutral.main}
                                        />
                                    </g>
                                </g>
                                <defs>
                                    <clipPath id="clip0_4069_27502">
                                        <rect
                                            width="16"
                                            height="16"
                                            fill={theme.palette.white}
                                        />
                                    </clipPath>
                                    <clipPath id="clip1_4069_27502">
                                        <rect
                                            width="16"
                                            height="16"
                                            fill={theme.palette.white}
                                        />
                                    </clipPath>
                                </defs>
                            </svg>
                            <Typography
                                variant={TypographyVariants.H5}
                                className={classes.benefitText2}
                            >
                                More Restful Sleep
                            </Typography>
                        </Box>
                        <Box className={`${classes.benefit} ${classes.benefitA}`}>
                            <svg
                                className={classes.icon4}
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M9.59508 6.42088C9.59508 6.42088 10.2853 2.361 7.46892 0.59845C7.42696 1.29602 7.23831 1.97689 6.91531 2.59659C6.59231 3.21629 6.14221 3.76088 5.59441 4.19479C4.39162 5.25232 2.12945 7.63014 2.15351 10.1653C2.14487 11.249 2.43924 12.3135 3.00342 13.2388C3.5676 14.1641 4.37913 14.9134 5.34645 15.402C5.38101 14.9127 5.51289 14.4352 5.73436 13.9975C5.95583 13.5598 6.26244 13.1707 6.63621 12.853C6.95294 12.6094 7.218 12.3052 7.41593 11.9582C7.61386 11.6111 7.74069 11.228 7.78904 10.8314C8.61705 11.2716 9.31665 11.9191 9.81939 12.7107C10.3221 13.5023 10.6108 14.4108 10.6572 15.3474V15.3595C11.5702 14.9412 12.3498 14.2786 12.9095 13.4448C13.4693 12.611 13.7875 11.6387 13.8289 10.6353C14.0287 8.25374 12.7242 5.01546 11.5667 3.95701C11.1297 4.93261 10.4511 5.78063 9.59508 6.42088Z"
                                    fill={theme.palette.neutral['600']}
                                />
                            </svg>
                            <Typography
                                variant={TypographyVariants.H5}
                                className={classes.benefitText2}
                            >
                                Weight Loss and Fitness Gains
                            </Typography>
                        </Box>
                        <Box className={`${classes.benefit} ${classes.benefitA}`}>
                            <svg
                                className={classes.icon5}
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <g clipPath="url(#clip0_4318_10941)">
                                    <path
                                        d="M8 0C3.6 0 0 3.6 0 8C0 12.4 3.6 16 8 16C12.4 16 16 12.4 16 8C16 3.6 12.4 0 8 0ZM6 5C6.6 5 7 5.4 7 6C7 6.6 6.6 7 6 7C5.4 7 5 6.6 5 6C5 5.4 5.4 5 6 5ZM8 12C6.3 12 5 10.7 5 9H11C11 10.7 9.7 12 8 12ZM10 7C9.4 7 9 6.6 9 6C9 5.4 9.4 5 10 5C10.6 5 11 5.4 11 6C11 6.6 10.6 7 10 7Z"
                                        fill={theme.palette.neutral.main}
                                    />
                                </g>
                                <defs>
                                    <clipPath id="clip0_4318_10941">
                                        <rect
                                            width="16"
                                            height="16"
                                            fill={theme.palette.white}
                                        />
                                    </clipPath>
                                </defs>
                            </svg>
                            <Typography
                                variant={TypographyVariants.H5}
                                className={classes.benefitText2}
                            >
                                Better Mood and Mental Health
                            </Typography>
                        </Box>
                        <Box className={`${classes.benefit} ${classes.benefitA}`}>
                            <svg
                                className={classes.icon6}
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <g clipPath="url(#clip0_4069_27531)">
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M5.14286 3.71426C5.14286 2.1363 6.42204 0.857117 8 0.857117C9.57796 0.857117 10.8571 2.1363 10.8571 3.71426C10.8571 5.29222 9.57796 6.5714 8 6.5714C6.42204 6.5714 5.14286 5.29222 5.14286 3.71426Z"
                                        fill={theme.palette.neutral.main}
                                    />
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M10.8571 5.99997C10.8571 4.73761 11.8805 3.71426 13.1429 3.71426C14.4052 3.71426 15.4286 4.73761 15.4286 5.99997C15.4286 7.26234 14.4052 8.28569 13.1429 8.28569C11.8805 8.28569 10.8571 7.26234 10.8571 5.99997Z"
                                        fill={theme.palette.neutral.main}
                                    />
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M0.571429 5.99997C0.571429 4.73761 1.59478 3.71426 2.85714 3.71426C4.11951 3.71426 5.14286 4.73761 5.14286 5.99997C5.14286 7.26234 4.11951 8.28569 2.85714 8.28569C1.59478 8.28569 0.571429 7.26234 0.571429 5.99997Z"
                                        fill={theme.palette.neutral.main}
                                    />
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M3.66466 10.0895C4.5774 8.66238 6.17779 7.71426 8 7.71426C9.82244 7.71426 11.423 8.66261 12.3357 10.09C12.9667 11.0769 13.2396 12.2668 13.1116 13.427C13.0916 13.6083 12.9862 13.7691 12.828 13.8599C11.4053 14.6763 9.75625 15.1428 8 15.1428C6.24375 15.1428 4.59473 14.6763 3.172 13.8599C3.01378 13.7691 2.90842 13.6083 2.88841 13.427C2.76033 12.2666 3.03346 11.0764 3.66466 10.0895Z"
                                        fill={theme.palette.neutral.main}
                                    />
                                    <path
                                        d="M2.72936 9.43135C2.72018 9.44545 2.71106 9.45958 2.702 9.47375C1.96641 10.6239 1.62977 11.9936 1.73311 13.344C1.26984 13.2736 0.82034 13.1604 0.388898 13.0085L0.30131 12.9776C0.14153 12.9214 0.0298875 12.7763 0.0164503 12.6074L0.00908429 12.5148C0.00306068 12.4391 0 12.3627 0 12.2857C0 10.7506 1.2107 9.49819 2.72936 9.43135Z"
                                        fill={theme.palette.neutral.main}
                                    />
                                    <path
                                        d="M14.2672 13.3439C14.3705 11.9939 14.034 10.6244 13.2987 9.47439C13.2895 9.46001 13.2802 9.44567 13.2709 9.43137C14.7894 9.49835 16 10.7506 16 12.2857C16 12.3627 15.9969 12.4391 15.9909 12.5148L15.9835 12.6074C15.9701 12.7763 15.8585 12.9214 15.6987 12.9776L15.6111 13.0085C15.1797 13.1604 14.7303 13.2736 14.2672 13.3439Z"
                                        fill={theme.palette.neutral.main}
                                    />
                                </g>
                                <defs>
                                    <clipPath id="clip0_4069_27531">
                                        <rect
                                            width="16"
                                            height="16"
                                            fill={theme.palette.white}
                                        />
                                    </clipPath>
                                </defs>
                            </svg>
                            <Typography
                                variant={TypographyVariants.H5}
                                className={classes.benefitText2}
                            >
                                Stronger Relationships
                            </Typography>
                        </Box>
                        <Box className={`${classes.benefit} ${classes.benefitA}`}>
                            <svg
                                className={classes.icon7}
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M9.82604 8.76896C10.6527 9.22177 11.6049 9.39172 12.5372 9.25288C13.4695 9.11405 14.3308 8.67405 14.9896 8.00001C14.5874 7.58828 14.1068 7.26116 13.5762 7.03791C13.0457 6.81465 12.4758 6.69977 11.9002 6.70001C11.1138 6.70126 10.3412 6.90628 9.65773 7.29509C8.97422 7.68389 8.40314 8.2432 8.00019 8.91846C7.59724 8.2432 7.02617 7.68389 6.34266 7.29509C5.65915 6.90628 4.88654 6.70126 4.10019 6.70001C3.52457 6.69977 2.9547 6.81465 2.42414 7.03791C1.89357 7.26116 1.41301 7.58828 1.01074 8.00001C1.66963 8.67405 2.53092 9.11405 3.46322 9.25288C4.39552 9.39172 5.34765 9.22177 6.17434 8.76896C6.738 9.23788 7.12745 9.88285 7.27999 10.6H4.75019C4.65584 10.6001 4.56263 10.6206 4.47703 10.6603C4.39143 10.7 4.3155 10.7579 4.25452 10.8299C4.19353 10.9019 4.14895 10.9863 4.12387 11.0772C4.09879 11.1682 4.09382 11.2635 4.10929 11.3566L4.75929 15.2566C4.78453 15.4084 4.86278 15.5463 4.98013 15.6458C5.09749 15.7453 5.24633 15.7999 5.40019 15.8H10.6002C10.7541 15.7999 10.9029 15.7453 11.0203 15.6458C11.1376 15.5463 11.2159 15.4084 11.2411 15.2566L11.8911 11.3566C11.9066 11.2635 11.9016 11.1682 11.8765 11.0772C11.8514 10.9863 11.8069 10.9019 11.7459 10.8299C11.6849 10.7579 11.609 10.7 11.5234 10.6603C11.4378 10.6206 11.3445 10.6001 11.2502 10.6H8.72039C8.87293 9.88285 9.26239 9.23788 9.82604 8.76896Z"
                                    fill={theme.palette.neutral.main}
                                />
                                <path
                                    d="M8.0002 0.200012C7.35741 0.200012 6.72905 0.390621 6.19459 0.747736C5.66013 1.10485 5.24357 1.61243 4.99759 2.20629C4.7516 2.80015 4.68724 3.45362 4.81264 4.08406C4.93805 4.71449 5.24758 5.29359 5.7021 5.74811C6.15662 6.20263 6.73572 6.51216 7.36615 6.63756C7.99659 6.76297 8.65006 6.69861 9.24392 6.45262C9.83778 6.20664 10.3454 5.79007 10.7025 5.25562C11.0596 4.72116 11.2502 4.0928 11.2502 3.45001C11.2492 2.58838 10.9064 1.76232 10.2972 1.15306C9.68789 0.543785 8.86183 0.201044 8.0002 0.200012ZM8.6502 4.10001C8.6502 4.459 8.35918 4.75001 8.0002 4.75001C7.64121 4.75001 7.3502 4.459 7.3502 4.10001V2.80001C7.3502 2.44103 7.64121 2.15001 8.0002 2.15001C8.35918 2.15001 8.6502 2.44103 8.6502 2.80001V4.10001Z"
                                    fill={theme.palette.neutral.main}
                                />
                            </svg>
                            <Typography
                                variant={TypographyVariants.H5}
                                className={classes.benefitText2}
                            >
                                Saved Money
                            </Typography>
                        </Box>
                    </Box>
                    <Typography
                        variant={TypographyVariants.H2}
                        className={classes.solution1}
                        dangerouslySetInnerHTML={{
                            __html: `Cutting back on <span style="font-weight:${TypographyWeights.BOLD};">drinking</span> is that habit` 
                        }}
                    />
                </Box>
            </Box>
        ),
        (
            <Box
                key={2}
                className={classes.subcontent}
            >
                <Typography
                    variant={TypographyVariants.H2}
                    className={classes.title3}
                    dangerouslySetInnerHTML={{
                        __html: `What if you could change <span style="font-weight:${TypographyWeights.BOLD};">one habit</span> to unlock improvements across every area of your life?` 
                    }}
                />
                <Box className={classes.sunnyside2}>
                    <Typography
                        variant={TypographyVariants.H2}
                        className={classes.sunnysideTitle}
                        dangerouslySetInnerHTML={{
                            __html: `With Sunnyside, we'll help you reach your <span style="color:${theme.palette.primary.main};">healthier drinking goals</span>, no matter what they are.` 
                        }}
                    />
                    <Typography
                        variant={TypographyVariants.H5}
                        className={classes.sunnysideSubtitle}
                        dangerouslySetInnerHTML={{
                            __html: 'Get a plan that\'s 100% personalized to you by completing this short quiz.' 
                        }}
                    />
                </Box>
                <Box className={classes.circleTakeover3}>
                    <Box className={classes.circleContent3}>
                        <Box className={`${classes.benefit} ${classes.benefitA}`}>
                            <svg
                                className={classes.iconA}
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <g clipPath="url(#clip0_4069_27486)">
                                    <path
                                        d="M9.34954 11.531C9.30153 11.595 9.23889 11.6465 9.16686 11.6812C9.09483 11.7159 9.01552 11.7329 8.93559 11.7306C8.85566 11.7284 8.77743 11.707 8.70747 11.6683C8.63752 11.6295 8.57787 11.5746 8.53354 11.508L6.06454 7.80402L5.49454 8.75702C5.45017 8.83106 5.38738 8.89236 5.31229 8.93495C5.2372 8.97753 5.15236 8.99995 5.06604 9.00002H1.97754C4.16254 12.317 7.99954 15.482 7.99954 15.482C7.99954 15.482 11.8365 12.317 14.0215 9.00002H11.2495L9.34954 11.531Z"
                                        fill={theme.palette.neutral.main}
                                    />
                                    <path
                                        d="M15.09 7.00001C15.341 6.41699 15.4801 5.79195 15.5 5.15751C15.5118 4.27359 15.2365 3.4097 14.7155 2.69557C14.1945 1.98144 13.4559 1.45561 12.6105 1.19705C11.7652 0.938489 10.8588 0.961156 10.0275 1.26164C9.1961 1.56213 8.48467 2.12422 8 2.86351C7.62576 2.3029 7.12059 1.8419 6.5282 1.52035C5.9358 1.1988 5.27399 1.02638 4.6 1.01801C3.50772 1.02435 2.46258 1.46378 1.69392 2.23984C0.925257 3.01591 0.495876 4.06522 0.5 5.15751C0.520083 5.79198 0.659338 6.41701 0.9105 7.00001H0V8.00001H4.783L6.0225 5.93451L8.976 10.365L10.75 8.00001H16V7.00001H15.09Z"
                                        fill={theme.palette.neutral.main}
                                    />
                                </g>
                                <defs>
                                    <clipPath id="clip0_4069_27486">
                                        <rect
                                            width="16"
                                            height="16"
                                            fill={theme.palette.white}
                                        />
                                    </clipPath>
                                </defs>
                            </svg>
                            <Typography
                                variant={TypographyVariants.H5}
                                className={classes.benefitText3}
                            >
                                Improved Overall Health
                            </Typography>
                        </Box>
                        <Box className={`${classes.benefit} ${classes.benefitA}`}>
                            <svg
                                className={classes.iconA}
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M8 16C12.4183 16 16 12.4183 16 8C16 3.58172 12.4183 0 8 0C3.58172 0 0 3.58172 0 8C0 12.4183 3.58172 16 8 16ZM8.8 4C8.8 3.55817 8.44183 3.2 8 3.2C7.55817 3.2 7.2 3.55817 7.2 4V8.8C7.2 9.06748 7.33368 9.31727 7.55624 9.46564L9.95624 11.0656C10.3239 11.3107 10.8206 11.2114 11.0656 10.8438C11.3107 10.4761 11.2114 9.97944 10.8438 9.73436L8.8 8.37185V4Z"
                                    fill={theme.palette.neutral.main}
                                />
                            </svg>
                            <Typography
                                variant={TypographyVariants.H5}
                                className={classes.benefitText3}
                            >
                                Increased Productivity
                            </Typography>
                        </Box>
                        <Box className={`${classes.benefit} ${classes.benefitA}`}>
                            <svg
                                className={classes.iconA}
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <g clipPath="url(#clip0_4069_27502)">
                                    <g clipPath="url(#clip1_4069_27502)">
                                        <path
                                            d="M6 0C2.5 0.9 0 4.1 0 7.9C0 12.4 3.6 16 8.1 16C11.9 16 15 13.5 16 10C9.9 11.7 4.3 6.1 6 0Z"
                                            fill={theme.palette.neutral.main}
                                        />
                                    </g>
                                </g>
                                <defs>
                                    <clipPath id="clip0_4069_27502">
                                        <rect
                                            width="16"
                                            height="16"
                                            fill={theme.palette.white}
                                        />
                                    </clipPath>
                                    <clipPath id="clip1_4069_27502">
                                        <rect
                                            width="16"
                                            height="16"
                                            fill={theme.palette.white}
                                        />
                                    </clipPath>
                                </defs>
                            </svg>
                            <Typography
                                variant={TypographyVariants.H5}
                                className={classes.benefitText3}
                            >
                                More Restful Sleep
                            </Typography>
                        </Box>
                        <Box className={`${classes.benefit} ${classes.benefitA}`}>
                            <svg
                                className={classes.iconA}
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M9.59508 6.42088C9.59508 6.42088 10.2853 2.361 7.46892 0.59845C7.42696 1.29602 7.23831 1.97689 6.91531 2.59659C6.59231 3.21629 6.14221 3.76088 5.59441 4.19479C4.39162 5.25232 2.12945 7.63014 2.15351 10.1653C2.14487 11.249 2.43924 12.3135 3.00342 13.2388C3.5676 14.1641 4.37913 14.9134 5.34645 15.402C5.38101 14.9127 5.51289 14.4352 5.73436 13.9975C5.95583 13.5598 6.26244 13.1707 6.63621 12.853C6.95294 12.6094 7.218 12.3052 7.41593 11.9582C7.61386 11.6111 7.74069 11.228 7.78904 10.8314C8.61705 11.2716 9.31665 11.9191 9.81939 12.7107C10.3221 13.5023 10.6108 14.4108 10.6572 15.3474V15.3595C11.5702 14.9412 12.3498 14.2786 12.9095 13.4448C13.4693 12.611 13.7875 11.6387 13.8289 10.6353C14.0287 8.25374 12.7242 5.01546 11.5667 3.95701C11.1297 4.93261 10.4511 5.78063 9.59508 6.42088Z"
                                    fill={theme.palette.neutral['600']}
                                />
                            </svg>
                            <Typography
                                variant={TypographyVariants.H5}
                                className={classes.benefitText3}
                            >
                                Weight Loss and Fitness Gains
                            </Typography>
                        </Box>
                        <Box className={`${classes.benefit} ${classes.benefitA}`}>
                            <svg
                                className={classes.iconA}
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <g clipPath="url(#clip0_4318_10941)">
                                    <path
                                        d="M8 0C3.6 0 0 3.6 0 8C0 12.4 3.6 16 8 16C12.4 16 16 12.4 16 8C16 3.6 12.4 0 8 0ZM6 5C6.6 5 7 5.4 7 6C7 6.6 6.6 7 6 7C5.4 7 5 6.6 5 6C5 5.4 5.4 5 6 5ZM8 12C6.3 12 5 10.7 5 9H11C11 10.7 9.7 12 8 12ZM10 7C9.4 7 9 6.6 9 6C9 5.4 9.4 5 10 5C10.6 5 11 5.4 11 6C11 6.6 10.6 7 10 7Z"
                                        fill={theme.palette.neutral.main}
                                    />
                                </g>
                                <defs>
                                    <clipPath id="clip0_4318_10941">
                                        <rect
                                            width="16"
                                            height="16"
                                            fill={theme.palette.white}
                                        />
                                    </clipPath>
                                </defs>
                            </svg>
                            <Typography
                                variant={TypographyVariants.H5}
                                className={classes.benefitText3}
                            >
                                Better Mood and Mental Health
                            </Typography>
                        </Box>
                        <Box className={`${classes.benefit} ${classes.benefitA}`}>
                            <svg
                                className={classes.iconA}
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <g clipPath="url(#clip0_4069_27531)">
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M5.14286 3.71426C5.14286 2.1363 6.42204 0.857117 8 0.857117C9.57796 0.857117 10.8571 2.1363 10.8571 3.71426C10.8571 5.29222 9.57796 6.5714 8 6.5714C6.42204 6.5714 5.14286 5.29222 5.14286 3.71426Z"
                                        fill={theme.palette.neutral.main}
                                    />
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M10.8571 5.99997C10.8571 4.73761 11.8805 3.71426 13.1429 3.71426C14.4052 3.71426 15.4286 4.73761 15.4286 5.99997C15.4286 7.26234 14.4052 8.28569 13.1429 8.28569C11.8805 8.28569 10.8571 7.26234 10.8571 5.99997Z"
                                        fill={theme.palette.neutral.main}
                                    />
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M0.571429 5.99997C0.571429 4.73761 1.59478 3.71426 2.85714 3.71426C4.11951 3.71426 5.14286 4.73761 5.14286 5.99997C5.14286 7.26234 4.11951 8.28569 2.85714 8.28569C1.59478 8.28569 0.571429 7.26234 0.571429 5.99997Z"
                                        fill={theme.palette.neutral.main}
                                    />
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M3.66466 10.0895C4.5774 8.66238 6.17779 7.71426 8 7.71426C9.82244 7.71426 11.423 8.66261 12.3357 10.09C12.9667 11.0769 13.2396 12.2668 13.1116 13.427C13.0916 13.6083 12.9862 13.7691 12.828 13.8599C11.4053 14.6763 9.75625 15.1428 8 15.1428C6.24375 15.1428 4.59473 14.6763 3.172 13.8599C3.01378 13.7691 2.90842 13.6083 2.88841 13.427C2.76033 12.2666 3.03346 11.0764 3.66466 10.0895Z"
                                        fill={theme.palette.neutral.main}
                                    />
                                    <path
                                        d="M2.72936 9.43135C2.72018 9.44545 2.71106 9.45958 2.702 9.47375C1.96641 10.6239 1.62977 11.9936 1.73311 13.344C1.26984 13.2736 0.82034 13.1604 0.388898 13.0085L0.30131 12.9776C0.14153 12.9214 0.0298875 12.7763 0.0164503 12.6074L0.00908429 12.5148C0.00306068 12.4391 0 12.3627 0 12.2857C0 10.7506 1.2107 9.49819 2.72936 9.43135Z"
                                        fill={theme.palette.neutral.main}
                                    />
                                    <path
                                        d="M14.2672 13.3439C14.3705 11.9939 14.034 10.6244 13.2987 9.47439C13.2895 9.46001 13.2802 9.44567 13.2709 9.43137C14.7894 9.49835 16 10.7506 16 12.2857C16 12.3627 15.9969 12.4391 15.9909 12.5148L15.9835 12.6074C15.9701 12.7763 15.8585 12.9214 15.6987 12.9776L15.6111 13.0085C15.1797 13.1604 14.7303 13.2736 14.2672 13.3439Z"
                                        fill={theme.palette.neutral.main}
                                    />
                                </g>
                                <defs>
                                    <clipPath id="clip0_4069_27531">
                                        <rect
                                            width="16"
                                            height="16"
                                            fill={theme.palette.white}
                                        />
                                    </clipPath>
                                </defs>
                            </svg>
                            <Typography
                                variant={TypographyVariants.H5}
                                className={classes.benefitText3}
                            >
                                Stronger Relationships
                            </Typography>
                        </Box>
                        <Box className={`${classes.benefit} ${classes.benefitA}`}>
                            <svg
                                className={classes.iconA}
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M9.82604 8.76896C10.6527 9.22177 11.6049 9.39172 12.5372 9.25288C13.4695 9.11405 14.3308 8.67405 14.9896 8.00001C14.5874 7.58828 14.1068 7.26116 13.5762 7.03791C13.0457 6.81465 12.4758 6.69977 11.9002 6.70001C11.1138 6.70126 10.3412 6.90628 9.65773 7.29509C8.97422 7.68389 8.40314 8.2432 8.00019 8.91846C7.59724 8.2432 7.02617 7.68389 6.34266 7.29509C5.65915 6.90628 4.88654 6.70126 4.10019 6.70001C3.52457 6.69977 2.9547 6.81465 2.42414 7.03791C1.89357 7.26116 1.41301 7.58828 1.01074 8.00001C1.66963 8.67405 2.53092 9.11405 3.46322 9.25288C4.39552 9.39172 5.34765 9.22177 6.17434 8.76896C6.738 9.23788 7.12745 9.88285 7.27999 10.6H4.75019C4.65584 10.6001 4.56263 10.6206 4.47703 10.6603C4.39143 10.7 4.3155 10.7579 4.25452 10.8299C4.19353 10.9019 4.14895 10.9863 4.12387 11.0772C4.09879 11.1682 4.09382 11.2635 4.10929 11.3566L4.75929 15.2566C4.78453 15.4084 4.86278 15.5463 4.98013 15.6458C5.09749 15.7453 5.24633 15.7999 5.40019 15.8H10.6002C10.7541 15.7999 10.9029 15.7453 11.0203 15.6458C11.1376 15.5463 11.2159 15.4084 11.2411 15.2566L11.8911 11.3566C11.9066 11.2635 11.9016 11.1682 11.8765 11.0772C11.8514 10.9863 11.8069 10.9019 11.7459 10.8299C11.6849 10.7579 11.609 10.7 11.5234 10.6603C11.4378 10.6206 11.3445 10.6001 11.2502 10.6H8.72039C8.87293 9.88285 9.26239 9.23788 9.82604 8.76896Z"
                                    fill={theme.palette.neutral.main}
                                />
                                <path
                                    d="M8.0002 0.200012C7.35741 0.200012 6.72905 0.390621 6.19459 0.747736C5.66013 1.10485 5.24357 1.61243 4.99759 2.20629C4.7516 2.80015 4.68724 3.45362 4.81264 4.08406C4.93805 4.71449 5.24758 5.29359 5.7021 5.74811C6.15662 6.20263 6.73572 6.51216 7.36615 6.63756C7.99659 6.76297 8.65006 6.69861 9.24392 6.45262C9.83778 6.20664 10.3454 5.79007 10.7025 5.25562C11.0596 4.72116 11.2502 4.0928 11.2502 3.45001C11.2492 2.58838 10.9064 1.76232 10.2972 1.15306C9.68789 0.543785 8.86183 0.201044 8.0002 0.200012ZM8.6502 4.10001C8.6502 4.459 8.35918 4.75001 8.0002 4.75001C7.64121 4.75001 7.3502 4.459 7.3502 4.10001V2.80001C7.3502 2.44103 7.64121 2.15001 8.0002 2.15001C8.35918 2.15001 8.6502 2.44103 8.6502 2.80001V4.10001Z"
                                    fill={theme.palette.neutral.main}
                                />
                            </svg>
                            <Typography
                                variant={TypographyVariants.H5}
                                className={classes.benefitText3}
                            >
                                Saved Money
                            </Typography>
                        </Box>
                    </Box>
                    <Typography
                        variant={TypographyVariants.H2}
                        className={classes.solution2}
                        dangerouslySetInnerHTML={{
                            __html: `Cutting back on <span style="font-weight:${TypographyWeights.BOLD};">drinking</span> is that habit` 
                        }}
                    />
                </Box>
            </Box>
        )
    ], [classes]);

    const onClick = () => {
        formContext.goNext();
    };

    return (
        <CardContainer
            sectionKey={'intro'}
            goNext={showButton && onClick}
            hasArrowButtonBorder
            shiftUp
        >
            <Card shiftUp>
                <Box className={classes.content}>
                    {(skippyEnvironment.has(ENVIRONMENT)) && (
                        <SunnysideButton
                            buttonVariant={ButtonVariants.PRIMARY}
                            buttonSize={ButtonSizes.LARGE}
                            onClick={formContext.skippySkipsIntro}
                            className={classes.skippy}
                        >
                            <Typography
                                style={{
                                    fontWeight: TypographyWeights.BOLD,
                                    color: theme.palette.white
                                }}
                            >
                                SKIPPY
                            </Typography>
                        </SunnysideButton>
                    )}
                    {CONTENT[step]}
                </Box>
            </Card>
        </CardContainer>
    );
};

export default IntroFlow;
